import { useRef, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdminPanelSettingsOutlined as AdminPanelSettingsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import { isHandoffImpersonation } from 'src/utils/adminImpersonation';
import endAdminImpersonation from 'src/utils/endAdminImpersonation';
import MenuPopover from './MenuPopover';

const ImpersonationButton = styled(IconButton)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: theme.palette.warning.light,
  color: theme.palette.warning.darker,
  border: `1px solid ${alpha(theme.palette.warning.dark, 0.16)}`,
  boxShadow: theme.shadows[1],
  '&:hover': {
    boxShadow: theme.shadows[3],
    backgroundColor: alpha(theme.palette.warning.light, 0.92),
  },
}));

const AdminImpersonationChip = () => {
  const { t } = useTranslation();
  const { impersonation } = useAuth();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  if (!impersonation) return null;

  const isHandoff = isHandoffImpersonation(impersonation);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleExit = () => endAdminImpersonation(impersonation);

  return (
    <>
      <Tooltip title={t('admin.impersonation.mode')}>
        <ImpersonationButton
          ref={anchorRef}
          onClick={handleOpen}
          size="small"
          aria-label={t('admin.impersonation.open-menu')}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : 'false'}
          aria-controls={open ? 'admin-impersonation-menu' : undefined}
        >
          <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />
        </ImpersonationButton>
      </Tooltip>

      <MenuPopover
        id="admin-impersonation-menu"
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 320 }}
      >
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography
            variant="overline"
            sx={{ color: 'warning.dark', letterSpacing: 1.1 }}
          >
            {t('admin.impersonation.mode')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {isHandoff
              ? t('admin.impersonation.handoff-description', {
                  name: impersonation.name,
                })
              : t('admin.impersonation.description')}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={handleExit}
          sx={{
            py: 1.2,
            px: 2.5,
            color: 'warning.darker',
            typography: 'body2',
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          {isHandoff
            ? t('admin.impersonation.exit')
            : t('admin.impersonation.return')}
        </MenuItem>
      </MenuPopover>
    </>
  );
};

export default AdminImpersonationChip;
